import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
import { getToken } from '@/store/modules/auth/shared';
import { getWsBrokerURL } from '@/utils/ws';

export type StompMessageHandler = (message: IMessage) => void;
export type StompUnsubscribe = () => void;

let client: Client | null = null;
const handlersByDestination = new Map<string, Set<StompMessageHandler>>();
const stompSubscriptions = new Map<string, StompSubscription>();

/** 最大重连次数，超过后停止重连 */
const MAX_RECONNECT_ATTEMPTS = 10;
let reconnectAttempts = 0;
let intentionalClose = false;

function ensureStompSubscription(destination: string) {
  if (!client?.connected || stompSubscriptions.has(destination)) {
    return;
  }

  const stompSub = client.subscribe(destination, message => {
    handlersByDestination.get(destination)?.forEach(handler => handler(message));
  });
  stompSubscriptions.set(destination, stompSub);
}

function resubscribeAll() {
  stompSubscriptions.forEach(sub => sub.unsubscribe());
  stompSubscriptions.clear();

  handlersByDestination.forEach((_handlers, destination) => {
    ensureStompSubscription(destination);
  });
}

/** 登录后建立 WebSocket 连接 */
export function connectStomp() {
  const token = getToken();
  if (!token) {
    return;
  }

  if (client?.active) {
    if (client.connected) {
      reconnectAttempts = 0;
      resubscribeAll();
    }
    return;
  }

  intentionalClose = false;
  reconnectAttempts = 0;

  client = new Client({
    brokerURL: getWsBrokerURL(token),
    reconnectDelay: 5000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    onConnect: () => {
      reconnectAttempts = 0;
      resubscribeAll();
    },
    onWebSocketClose: () => {
      if (intentionalClose) return;
      reconnectAttempts++;
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        intentionalClose = true;
        client?.deactivate();
        client = null;
      }
    }
  });

  client.activate();
}

/** 退出登录时断开 WebSocket */
export function disconnectStomp() {
  intentionalClose = true;

  stompSubscriptions.forEach(sub => sub.unsubscribe());
  stompSubscriptions.clear();
  handlersByDestination.clear();

  if (client) {
    client.deactivate();
    client = null;
  }
  reconnectAttempts = 0;
}

/**
 * 订阅 STOMP destination，连接建立前注册会在 onConnect 后自动订阅。
 * @returns 取消订阅函数
 */
export function subscribeStomp(
  destination: string,
  handler: StompMessageHandler
): StompUnsubscribe {
  if (!handlersByDestination.has(destination)) {
    handlersByDestination.set(destination, new Set());
  }

  handlersByDestination.get(destination)!.add(handler);
  ensureStompSubscription(destination);

  return () => {
    const handlers = handlersByDestination.get(destination);
    if (!handlers) {
      return;
    }

    handlers.delete(handler);
    if (handlers.size > 0) {
      return;
    }

    handlersByDestination.delete(destination);
    stompSubscriptions.get(destination)?.unsubscribe();
    stompSubscriptions.delete(destination);
  };
}

export function isStompConnected() {
  return Boolean(client?.connected);
}

/** 等待 WebSocket 连接就绪 */
export function waitForStompConnected(timeoutMs = 5000): Promise<boolean> {
  if (isStompConnected()) {
    return Promise.resolve(true);
  }

  connectStomp();

  return new Promise(resolve => {
    const start = Date.now();
    const timer = window.setInterval(() => {
      if (isStompConnected()) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() - start >= timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}
